/**
 * teamsMultiWindow.ts — Teams pop-out (Stageview) + beforeunload close-guard.
 *
 * v0.1.26 ripped out the naive popoutCurrent() because it used window.open(),
 * which escapes the Teams iframe to the default browser. This file now uses
 * the correct Teams SDK path: stageView.open({ openMode: 'popout' }).
 *
 * Stageview is the official, documented multi-window API. It opens the
 * current route in a floating Teams window (not a browser tab). Requires:
 *   - Teams desktop client (web host falls back to modal and we reject)
 *   - manifestVersion >= 1.11 (we're on 1.19)
 *   - staticTabs[].supportedSizes declared (set in manifest.json)
 *
 * The beforeunload close-guard is retained from v0.1.26 — it warns on
 * accidental tab close when in-flight work exists.
 */

import { app, stageView, pages, FrameContexts, HostClientType } from '@microsoft/teams-js';

/** Teams manifest id — must match appPackage/manifest.json `id`. */
const APP_ID = 'd8c7f507-3f20-40b3-bde1-a8af589a1ef1';

// ---------------------------------------------------------------------------
// Pop-out via Stageview
// ---------------------------------------------------------------------------

export interface PopoutResult {
  ok: boolean;
  error?: string;
  reason?: 'not-in-teams' | 'not-desktop' | 'already-popout' | 'sdk-error';
}

/**
 * Opens the current route in a new Teams floating window via Stageview.
 * Returns {ok:true} on success, {ok:false, reason} otherwise so callers can
 * decide whether to show a toast. DESKTOP ONLY — reason='not-desktop' on web.
 */
/**
 * @param openTarget a deep-link target ("cart:<guid>") to carry into the pop-out. Teams does NOT
 * pass subEntityId into a Stageview window (LWP, 2026-10-14), so it rides on the contentUrl as
 * ?open=<target> and useTeamsDeepLink reads it there.
 */
export async function popoutCurrent(openTarget?: string | null): Promise<PopoutResult> {
  try {
    // Ensure SDK is initialized. Safe to call repeatedly.
    try {
      await app.initialize();
    } catch {
      return { ok: false, reason: 'not-in-teams' };
    }

    const ctx = await app.getContext();

    // Desktop only. Pop-out windows don't exist on web/mobile.
    const clientType = ctx.app?.host?.clientType;
    const isDesktop =
      clientType === HostClientType.desktop || clientType === HostClientType.macos;
    if (!isDesktop) {
      return { ok: false, reason: 'not-desktop' };
    }

    // If we're already inside a pop-out window, no-op. Check two signals:
    //   1. ctx.page.isMultiWindow — the canonical Teams SDK bool for "this
    //      frame is a secondary (pop-out / task module / stage) window."
    //      Per MS Learn + Teams SDK docs (post-2022). Fixed smoke test POPOUT-03.
    //   2. ctx.page.frameContext === 'stage' — legacy fallback for older SDK
    //      hosts that don't populate isMultiWindow. Redundant but cheap.
    if (ctx.page?.isMultiWindow || ctx.page?.frameContext === FrameContexts.stage) {
      return { ok: false, reason: 'already-popout' };
    }

    const url = new URL(window.location.href);
    if (openTarget) url.searchParams.set('open', openTarget);
    await stageView.open({
      appId: APP_ID,
      contentUrl: url.toString(),
      title: 'PCB Retail',
      openMode: stageView.StageViewOpenMode.popout,
    });

    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      reason: 'sdk-error',
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

// ---------------------------------------------------------------------------
// Pop-out detection (cached)
// ---------------------------------------------------------------------------

let popoutDetectionPromise: Promise<boolean> | null = null;

/**
 * True when the current frame is a Teams pop-out / secondary window.
 * Used by TopBar to hide the pop-out button once we're already in one.
 *
 * Detection priority (fixed after smoke-test POPOUT-03 finding 2026-04-23):
 *   1. ctx.page.isMultiWindow — canonical bool per MS Learn. True in any
 *      secondary window spawned via stageView / task-module / pop-out.
 *   2. ctx.page.frameContext === 'stage' — legacy fallback for SDK hosts
 *      that don't populate isMultiWindow yet.
 *
 * Previously we only checked frameContext === 'stage', which missed the
 * pop-out in Bill's Teams desktop build (frameContext stayed 'content').
 * The PopOutButton therefore appeared inside the popped-out window — wrong.
 * Result is cached for the life of the page.
 */
export function isInPopoutWindow(): Promise<boolean> {
  if (popoutDetectionPromise) return popoutDetectionPromise;

  popoutDetectionPromise = (async () => {
    if (typeof window === 'undefined') return false;
    try {
      await app.initialize();
      const ctx = await app.getContext();
      if (ctx.page?.isMultiWindow) return true;
      if (ctx.page?.frameContext === FrameContexts.stage) return true;
      return false;
    } catch {
      // Not in Teams or SDK unavailable — not a pop-out.
      return false;
    }
  })();

  return popoutDetectionPromise;
}

// ---------------------------------------------------------------------------
// Host-environment detection (for the PopOutGate)
// ---------------------------------------------------------------------------

export type HostEnvironment =
  | 'teams-desktop-main'    // Desktop Teams, primary tab frame  → gate shows
  | 'teams-desktop-popout'  // Desktop Teams, secondary (stage) window → app renders
  | 'teams-web'             // Browser-hosted Teams → app renders (popout n/a)
  | 'teams-mobile'          // iOS/Android Teams → app renders (popout n/a)
  | 'browser'               // Direct browser dev session → app renders
  | 'unknown';

export interface HostEnvironmentInfo {
  env: HostEnvironment;
  clientType?: string;     // raw ctx.app.host.clientType for diagnostics
  frameContext?: string;   // raw ctx.page.frameContext
  isMultiWindow: boolean;
}

/**
 * Classify the current host environment so the PopOutGate knows whether to
 * show the "please pop out" landing. Only `teams-desktop-main` triggers the
 * gate — every other env just renders the app.
 */
export async function detectHostEnvironment(): Promise<HostEnvironmentInfo> {
  if (typeof window === 'undefined') {
    return { env: 'unknown', isMultiWindow: false };
  }
  try {
    await app.initialize();
    const ctx = await app.getContext();
    const clientType = ctx.app?.host?.clientType;
    const frameContext = ctx.page?.frameContext;
    const isMultiWindow = Boolean(ctx.page?.isMultiWindow) || frameContext === FrameContexts.stage;

    const isDesktop =
      clientType === HostClientType.desktop || clientType === HostClientType.macos;
    const isWeb = clientType === HostClientType.web;
    const isMobile = clientType === HostClientType.android || clientType === HostClientType.ios;

    if (isDesktop) {
      return {
        env: isMultiWindow ? 'teams-desktop-popout' : 'teams-desktop-main',
        clientType, frameContext, isMultiWindow,
      };
    }
    if (isWeb) return { env: 'teams-web', clientType, frameContext, isMultiWindow };
    if (isMobile) return { env: 'teams-mobile', clientType, frameContext, isMultiWindow };
    return { env: 'unknown', clientType, frameContext, isMultiWindow };
  } catch {
    // SDK init failed → direct browser session (dev mode). App renders normally.
    return { env: 'browser', isMultiWindow: false };
  }
}

// ---------------------------------------------------------------------------
// Host navigation (post-popout redirect of the main window)
// ---------------------------------------------------------------------------

/** Teams first-party app IDs. Used to redirect the main window away from our
 *  landing screen after a successful popout spawn. */
export const TEAMS_APP_IDS = {
  chat: '14d6962d-6eeb-4f48-8890-de55454bb136',
  calls: '20c3440d-c67e-4420-9f80-0e50c39693df',
  activity: 'activity', // deep-link only
} as const;

/**
 * Navigate the HOST Teams window (not our popout) to another Teams app.
 * Used after a popout is spawned: main window becomes redundant so we
 * redirect it to Chat (or Calls) so the officer can keep working in Teams
 * while the Retail app runs in the popped-out window.
 *
 * Returns {ok:true} on successful navigation, {ok:false, error} otherwise.
 * Failure is non-fatal — main window simply stays on the Retail landing.
 */
export async function navigateHostToApp(
  appId: string,
  pageId = 'chatList',
): Promise<{ ok: boolean; error?: string }> {
  try {
    if (!pages.isSupported?.()) return { ok: false, error: 'pages namespace unsupported' };
    await pages.navigateToApp({ appId, pageId });
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

// ---------------------------------------------------------------------------
// Close-guard (kept from v0.1.26, untouched)
// ---------------------------------------------------------------------------

/** Predicate set by SessionContext — true when in-flight work exists. */
let unloadGuardPredicate: () => boolean = () => false;

export function setUnloadGuardPredicate(fn: () => boolean): void {
  unloadGuardPredicate = fn;
}

/**
 * Install the best-effort beforeunload close-guard. Predicate-gated on
 * in-flight work — empty sessions never prompt. Teams desktop may strip
 * the event; in that case the predicate simply has no UI effect. Browser-
 * direct dev sessions see the browser's native "Changes you made may not
 * be saved" dialog.
 */
let unloadHandlerInstalled = false;
export function installCloseGuard(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  if (unloadHandlerInstalled) return () => undefined;

  const handler = (ev: BeforeUnloadEvent): string | undefined => {
    if (!unloadGuardPredicate()) return undefined;
    ev.preventDefault();
    ev.returnValue = '';
    return '';
  };

  window.addEventListener('beforeunload', handler);
  unloadHandlerInstalled = true;

  return () => {
    window.removeEventListener('beforeunload', handler);
    unloadHandlerInstalled = false;
  };
}
